import { useState } from 'react';
import { Download } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { formatINR } from '@vj/shared';
import { api, downloadFile } from '../../lib/api.js';
import { Button } from '../../components/ui/Button.jsx';
import { TextField } from '../../components/ui/Field.jsx';
import { todayIso, currentFyStart } from './dateRange.js';

function SupplyTypeCard({ label, totals }) {
  return (
    <div className="rounded-card border border-border p-4">
      <p className="text-xs uppercase tracking-wide text-muted">{label}</p>
      <p className="tabular mt-1 text-lg font-semibold">{formatINR(totals.taxableValue)}</p>
      <p className="text-xs text-muted">{totals.count} invoice(s)</p>
      <div className="tabular mt-2 flex gap-3 text-xs text-muted">
        <span>CGST {formatINR(totals.cgstAmount)}</span>
        <span>SGST {formatINR(totals.sgstAmount)}</span>
        <span>IGST {formatINR(totals.igstAmount)}</span>
      </div>
    </div>
  );
}

export default function GstSalesRegisterPage() {
  const [from, setFrom] = useState(currentFyStart());
  const [to, setTo] = useState(todayIso());
  const [downloading, setDownloading] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['reports', 'gst-sales-register', from, to],
    queryFn: () => api.get('/reports/gst-sales-register', { query: { from, to } }),
  });

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadFile('/reports/exports/gst-sales-register', 'gst-sales-register.csv', { query: { from, to } });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">GST sales register</h1>
          <p className="mt-1 text-sm text-muted">B2B/B2C split and HSN-wise summary, for GSTR-1 reconciliation.</p>
        </div>
        <Button variant="secondary" size="sm" loading={downloading} onClick={handleDownload}>
          <Download size={16} aria-hidden="true" /> CSV
        </Button>
      </div>

      <div className="card flex flex-wrap items-end gap-3 p-4">
        <TextField label="From" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        <TextField label="To" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
      </div>

      {!isLoading && data ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <SupplyTypeCard label="B2B (has GSTIN)" totals={data.b2b} />
          <SupplyTypeCard label="B2C (walk-in)" totals={data.b2c} />
        </div>
      ) : null}

      <section className="card table-scroll">
        <table className="w-full min-w-[600px] text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted">
              <th className="px-4 py-3">HSN code</th>
              <th className="px-4 py-3 text-right">GST rate</th>
              <th className="px-4 py-3 text-right">Taxable value</th>
              <th className="px-4 py-3 text-right">CGST</th>
              <th className="px-4 py-3 text-right">SGST</th>
              <th className="px-4 py-3 text-right">IGST</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-muted">Loading…</td></tr>
            ) : !data?.hsnSummary.length ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-muted">No sales in this period.</td></tr>
            ) : (
              data.hsnSummary.map((r) => (
                <tr key={`${r.hsnCode}-${r.gstRate}`} className="tabular border-b border-border last:border-0">
                  <td className="px-4 py-3 font-medium">{r.hsnCode}</td>
                  <td className="px-4 py-3 text-right">{r.gstRate}%</td>
                  <td className="px-4 py-3 text-right">{formatINR(r.taxableValue)}</td>
                  <td className="px-4 py-3 text-right">{formatINR(r.cgstAmount)}</td>
                  <td className="px-4 py-3 text-right">{formatINR(r.sgstAmount)}</td>
                  <td className="px-4 py-3 text-right">{formatINR(r.igstAmount)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}
